import { reactive, computed, watch } from 'vue';
import type { RouteStop, TripMode, TripState } from '@/types';
import {
  loadFromLocalStorage,
  loadFromParams,
  saveToLocalStorage,
  updateURL,
  buildShareURL,
} from '@/core/persistence';
import { buildCalendar } from '@/core/calendar';
import { DEFAULT_STATE } from '@/core/constants';
import { useAnalytics } from '@/composables/useAnalytics';

/**
 * Store réactif central pour l'état du voyage.
 *
 * Vue choix : pas de Pinia.
 *   À l'échelle d'1 store + ~10 actions, un `reactive()` exporté est plus simple
 *   et tout aussi performant. Si on multipliait les stores, Pinia se justifierait.
 *
 * Pattern :
 *   - `state` est une `reactive()` mutable depuis n'importe quel composant
 *   - les actions exportées sont les seules à muter `state.routeStops` etc.
 *     (pour pouvoir tracer / brancher des side-effects)
 *   - `calendar` est un computed dérivé en temps réel
 *   - une `watch` profonde persiste le state (localStorage + URL debounced)
 *
 * Singleton : `useTrip()` renvoie toujours le même `state` (créé au premier appel).
 */

// ---------- Singleton ----------

let _store: ReturnType<typeof createStore> | null = null;

export function useTrip() {
  if (!_store) _store = createStore();
  return _store;
}

function createStore() {
  // -------------- state --------------

  const state = reactive<TripState>({
    origin: DEFAULT_STATE.origin,
    maxHours: DEFAULT_STATE.maxHours,
    routeStops: [],
    routeMode: DEFAULT_STATE.routeMode,
    routeDirectOnly: DEFAULT_STATE.routeDirectOnly,
    tripStartDate: null,
    tripStartTime: DEFAULT_STATE.tripStartTime,
  });

  // -------------- restauration (URL > localStorage) --------------

  const urlLoaded = loadFromParams(state, window.location.search);
  if (!urlLoaded) {
    loadFromLocalStorage(state);
  } else {
    // Cohérence : un lien partagé devient aussi l'état local
    saveToLocalStorage(state);
  }

  // -------------- persistence (debounced URL, immédiate localStorage) --------------

  let urlTimer: ReturnType<typeof setTimeout> | null = null;

  watch(
    () => state,
    () => {
      saveToLocalStorage(state);
      if (urlTimer) clearTimeout(urlTimer);
      urlTimer = setTimeout(() => updateURL(state), 250);
    },
    { deep: true },
  );

  // -------------- computed --------------

  /**
   * Calendrier dérivé du state. Recalculé à chaque mutation.
   * Composants consomment `calendar.value` directement.
   *
   * Coût : un buildCalendar = quelques Dijkstra (1 par segment d'étape).
   * À 5-10 étapes c'est négligeable ; à 50+ on pourrait throttler.
   */
  const calendar = computed(() => buildCalendar(state));

  // -------------- actions --------------

  /**
   * Ajoute une étape à la fin du parcours.
   * Renvoie `false` si l'ajout a été refusé (doublon consécutif), `true` sinon.
   * Les appelants peuvent utiliser ce retour pour afficher un feedback à l'utilisateur.
   */
  function addStop(id: string): boolean {
    // Empêche les doublons consécutifs (cliquer 2x sur la même ville par
    // erreur, ou un lien partagé qui contient `?route=paris,paris,rome`).
    // Une ville peut apparaître plusieurs fois dans l'itinéraire — c'est
    // courant pour une boucle qui repasse par la base (paris,...,paris) —
    // mais pas DEUX FOIS DE SUITE : ça n'a aucun sens géographique.
    const last = state.routeStops[state.routeStops.length - 1];
    if (last && last.id === id) return false;

    state.routeStops.push({ id, nights: 0 });

    // Analytics
    const { trackEvent } = useAnalytics();
    trackEvent('itinerary_stop_added', {
      stop_id: id,
      total_stops: state.routeStops.length,
    });
    // KPI Nord : passage à 2 étapes = utilisateur a vraiment "complété" un mini-itinéraire
    if (state.routeStops.length === 2) {
      trackEvent('itinerary_completed', { total_stops: 2 });
    }
    return true;
  }

  function removeStop(index: number): void {
    state.routeStops.splice(index, 1);
  }

  function moveStop(index: number, direction: 'up' | 'down'): void {
    const j = direction === 'up' ? index - 1 : index + 1;
    if (j < 0 || j >= state.routeStops.length) return;
    [state.routeStops[index], state.routeStops[j]] = [
      state.routeStops[j],
      state.routeStops[index],
    ];
  }

  function setNights(index: number, nights: number): void {
    const stop = state.routeStops[index];
    if (!stop) return;
    stop.nights = Math.max(0, nights);
  }

  function clearRoute(): void {
    state.routeStops = [];
  }

  function setMode(mode: TripMode): void {
  state.routeMode = mode;

  // Analytics
  const { trackEvent } = useAnalytics();
  trackEvent('map_mode_changed', { mode });
}

  function setDirectOnly(value: boolean): void {
    state.routeDirectOnly = value;
  }

  function setMaxHours(hours: number): void {
    state.maxHours = Math.max(1, Math.min(24, Math.round(hours)));
  }

  function setStartDate(date: string | null): void {
    // null = effacer la date. Une chaîne doit être au format YYYY-MM-DD.
    if (date === null) {
      state.tripStartDate = null;
      return;
    }
    if (!/^\d{4}-\d{2}-\d{2}$/.test(date)) return;
    state.tripStartDate = date;
  }

  function setStartTime(time: string): void {
    // Validation HH:MM — silencieux si invalide pour ne pas casser le calendrier.
    // L'UI passe toujours un format valide depuis <input type="time">, c'est une
    // ceinture & bretelles pour les appels programmatiques (ou un cas tordu de Safari).
    if (!/^\d{2}:\d{2}$/.test(time)) return;
    state.tripStartTime = time;
  }

  function setOrigin(id: string): void {
    state.origin = id;
  }

  /** Remplace l'ordre des étapes (utilisé par le TSP) */
  function replaceStops(stops: RouteStop[]): void {
    // Sécurité : déduplique les doublons consécutifs au passage. Le TSP et
    // les autres appelants ne devraient pas en créer, mais on protège.
    const clean: RouteStop[] = [];
    for (const s of stops) {
      const last = clean[clean.length - 1];
      if (last && last.id === s.id) continue;
      clean.push(s);
    }
    state.routeStops = clean;
  }

  // -------------- actions overrides par étape --------------

  /**
   * Définit (ou retire) une heure de départ spécifique pour une étape.
   * Passer `null` ou une chaîne vide réinitialise l'héritage de `tripStartTime`.
   * La chaîne doit être au format "HH:MM", sinon l'appel est ignoré.
   */
  function setStopStartTime(index: number, time: string | null): void {
    const stop = state.routeStops[index];
    if (!stop) return;
    if (!time) {
      delete stop.startTime;
      return;
    }
    if (!/^\d{2}:\d{2}$/.test(time)) return;
    stop.startTime = time;
  }

  /**
   * Définit (ou retire) un override de mode pour le segment partant de cette étape.
   * Passer `null` réinitialise l'héritage du `routeMode` global.
   */
  function setStopModeOverride(index: number, mode: TripMode | null): void {
    const stop = state.routeStops[index];
    if (!stop) return;
    if (mode === null) {
      delete stop.modeOverride;
      return;
    }
    stop.modeOverride = mode;
  }

  function getShareURL(): string {
  // Analytics
  const { trackEvent } = useAnalytics();
  trackEvent('itinerary_shared', {
    total_stops: state.routeStops.length,
    mode: state.routeMode,
  });
  return buildShareURL(state);
}

  // -------------- exposed API --------------

  return {
    state,
    calendar,
    urlLoaded,
    // actions
    addStop,
    removeStop,
    moveStop,
    setNights,
    clearRoute,
    setMode,
    setDirectOnly,
    setMaxHours,
    setStartDate,
    setStartTime,
    setOrigin,
    replaceStops,
    setStopStartTime,
    setStopModeOverride,
    getShareURL,
  };
}
